import {groupByUniqueKey, objectFromEntries} from '@benchling/js-utils';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineEdgeUpsert} from 'pipelines/network/PipelineDAO';
import {PipelineEdge, PipelineGraphQL, PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {getLatestStepVersion, getPossibleEdges} from 'pipelines/StepConnectionUtils';
import removeNulls from 'util/removeNulls';
import {JSONValue} from 'util/Types';

type EdgeToNewStep = Extract<PipelineEdgeUpsert, {fromStepId: string}>;

type EdgeFromNewStep = Extract<PipelineEdgeUpsert, {fromStepIndex: number}>;

/**
 * Convert a `PipelineEdge` to a `PipelineEdgeUpsert`, without modification. This is necessary when modifying
 * an existing step's `incomingEdges`, since any edges not specified in the request will be removed.
 */
function createEdgeUpsertWithoutChanges(edge: PipelineEdge): PipelineEdgeUpsert {
  return {
    fromStepId: edge.fromStep.id,
    fromStepDataName: edge.fromStepData.name,
    fromStepDataIndex: edge.fromStepDataIndex,
    toStepDataName: edge.toStepData.name,
    toStepDataIndex: edge.toStepDataIndex,
  };
}

/**
 * Create an edge from an existing step and step data, to the new step.
 *
 * Which input step data on the new step to connect to is inferred, based on the step data kind. Connecting
 * `isMulti` and non-`isMulti` step datas, or `isRequired` and non-`isRequired` step datas is allowed.
 *
 * If multiple connections are possible (e.g. to `Left`/`Right` on `JOIN`), the first option is used.
 * If no connections are possible, `null` is returned.
 */
function createEdgeUpsertToNewStep(
  toStepType: PipelineStepType,
  fromStep: Pick<PipelineStep, 'id' | 'stepType'>,
  fromStepData: Pick<PipelineStepData, 'name'>,
  fromStepDataIndex: number | null = null,
  toStepDataIndex: number | null = null
): EdgeToNewStep | null {
  const possibleEdges = getPossibleEdges({
    fromStepType: fromStep.stepType,
    fromStepData: {systemName: fromStepData.name},
    toStepType,
  });
  return possibleEdges.length === 0
    ? null
    : {
        fromStepId: fromStep.id,
        fromStepDataName: fromStepData.name,
        fromStepDataIndex,
        toStepDataName: possibleEdges[0].toStepData.systemName,
        toStepDataIndex: possibleEdges[0].toStepData.isMulti ? toStepDataIndex : 0,
      };
}

/**
 * Create an edge from the new step, to an existing step and step data.
 *
 * Which output step data on the new step to connect from is inferred, based on the step data kind. Connecting
 * `isMulti` and non-`isMulti` step datas, or `isRequired` and non-`isRequired`, step datas is allowed.
 *
 * If multiple connections are possible (e.g. from `Output dataset`/`Sampled output dataset` on `CHART_VIEW`),
 * the first option is used. If no connections are possible, `null` is returned.
 */
function createEdgeUpsertFromNewStep(
  fromStepType: PipelineStepType,
  toStep: Pick<PipelineStep, 'id' | 'stepType'>,
  toStepData: Pick<PipelineStepData, 'name'>,
  toStepDataIndex: number | null = null,
  fromStepDataIndex: number | null = null
): EdgeFromNewStep | null {
  const possibleEdges = getPossibleEdges({
    fromStepType,
    toStepType: toStep.stepType,
    toStepData: {systemName: toStepData.name},
  });
  return possibleEdges.length === 0
    ? null
    : {
        fromStepIndex: 0,
        fromStepDataName: possibleEdges[0].fromStepData.systemName,
        fromStepDataIndex: possibleEdges[0].fromStepData.isMulti ? fromStepDataIndex : 0,
        toStepDataName: toStepData.name,
        toStepDataIndex,
      };
}

/**
 * Re-connect the `incomingEdges` of `toStep`, so that edges that flow from `fromStepDataToReplace` instead
 * flow from an output step data on new step, `newStepType`.
 *
 * This will make `toStep` a child of the new step, `newStepType`.
 *
 * All edges of `toStep` are included in the return value, including those that do not need to be updated.
 *
 * If an edge cannot be re-connected, it will not be updated.
 */
function reconnectEdgesOfFromStepData(
  newStepType: PipelineStepType,
  fromStepDataToReplace: PipelineStepData,
  fromStepDataIndex: number | null,
  toStep: PipelineStep
): Array<PipelineEdgeUpsert> {
  const shouldReconnectEdge = (edge: PipelineEdge): boolean =>
    edge.fromStepData.id === fromStepDataToReplace.id &&
    (edge.fromStepDataIndex === fromStepDataIndex ||
      edge.fromStepDataIndex == null ||
      fromStepDataIndex == null);

  return toStep.incomingEdges.map((edge) =>
    shouldReconnectEdge(edge)
      ? (createEdgeUpsertFromNewStep(
          newStepType,
          toStep,
          edge.toStepData,
          edge.toStepDataIndex,
          edge.fromStepDataIndex
        ) ?? createEdgeUpsertWithoutChanges(edge))
      : createEdgeUpsertWithoutChanges(edge)
  );
}

export interface AddStepWithInferredEdgesArgs {
  /** The step type to add */
  stepType: PipelineStepType;
  /** The step data to connect the new step to */
  stepData?: PipelineStepData;
  /** The raw data within `stepData` to connect the new step to */
  stepDataIndex?: number | null;
  /** Config for the new step */
  config?: Record<string, JSONValue>;
  /** Whether to immediately execute the new step */
  runAfterCreate?: boolean;
}

/**
 * Add a new step to the pipeline.
 *
 * If `stepData` is specified, the new step will be connected to `stepData`; any edges currently connected to
 * `stepData` will be re-connected onto the new step.
 *
 * This allows users to add a step "in-line", in the middle of the pipeline. (The alternate experience, which
 * is not yet supported, would be to create a new branch point off of `stepData`, and not re-connect any
 * existing edges.)
 *
 * Given ambiguous ways to re-connect steps, this function prefers to make a reasonable guess, rather than
 * raise an exception, on the assumption users can drag-and-drop edges, to fix any mistakes.
 *
 * @returns {stepId} The ID of the new step
 */
export async function addStepWithInferredEdges(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>,
  {stepType, stepData, stepDataIndex, config, runAfterCreate}: AddStepWithInferredEdgesArgs
): Promise<{stepId: string}> {
  const stepsById = groupByUniqueKey(pipeline.steps, (step) => step.id);

  const fromStep =
    stepData && pipeline.steps.find((step) => step.outputStepDatas.some(({id}) => id === stepData.id));

  const toStep =
    stepData && pipeline.steps.find((step) => step.inputStepDatas.some(({id}) => id === stepData.id));

  let edgesToNewStep: Array<EdgeToNewStep | null> = [];
  let edgesFromNewStepByStepId: {[stepId: string]: Array<PipelineEdgeUpsert | null>} = {};

  // If `stepData` is an output step data...
  if (fromStep) {
    // ... create an edge that flows from `stepData` to the new step, and
    edgesToNewStep = [createEdgeUpsertToNewStep(stepType, fromStep, stepData, stepDataIndex)];
    // ... re-connect edges that flow from `stepData` to instead flow from the new step.
    edgesFromNewStepByStepId = objectFromEntries(
      fromStep.outgoingEdges
        .filter(({fromStepData}) => fromStepData.id === stepData?.id)
        .map((edge) => [
          edge.toStep.id,
          reconnectEdgesOfFromStepData(stepType, stepData, stepDataIndex ?? null, stepsById[edge.toStep.id]),
        ])
    );
  }

  // If `stepData` is an input step data...
  if (toStep) {
    // ... re-connect edges that flow to `stepData` to instead flow to the new step, and
    edgesToNewStep = toStep.incomingEdges
      .filter(({toStepData}) => toStepData.id === stepData?.id)
      .filter(
        ({toStepDataIndex}) =>
          toStepDataIndex === stepDataIndex || toStepDataIndex == null || stepDataIndex == null
      )
      // If the existing `stepData` is multi-input, but the new `stepType` has a single-input step data, this
      // may re-connect excess edges to `stepType`, which the user will have to manually resolve.
      .map((edge) =>
        createEdgeUpsertToNewStep(
          stepType,
          stepsById[edge.fromStep.id],
          edge.fromStepData,
          edge.fromStepDataIndex,
          edge.toStepDataIndex
        )
      );
    // ... create an edge the flows from the new step to `stepData`, while maintaining edges that flow to
    // other step datas.
    edgesFromNewStepByStepId = {
      [toStep.id]: [
        createEdgeUpsertFromNewStep(stepType, toStep, stepData, stepDataIndex),
        ...toStep.incomingEdges
          .filter(
            ({toStepData, toStepDataIndex}) =>
              !(
                toStepData.id === stepData?.id &&
                (toStepDataIndex === stepDataIndex || toStepDataIndex == null || stepDataIndex == null)
              )
          )
          .map((edge) => createEdgeUpsertWithoutChanges(edge)),
      ],
    };
  }

  const {newStepIds} = await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: [
      {
        stepType,
        stepVersion: getLatestStepVersion(stepType),
        incomingEdges: removeNulls(edgesToNewStep),
        config,
        runAfterCreate,
      },
    ],
    stepsToUpdate: Object.entries(edgesFromNewStepByStepId).map(([stepId, incomingEdges]) => ({
      stepId,
      incomingEdges: removeNulls(incomingEdges),
    })),
  });

  return {stepId: newStepIds[0]};
}
